/**
 * The frame every page sits in (admin-design-r1, navigation A). Drawn from a section registry, so a new section is
 * one entry: grouped sidebar on a desk, an icon rail under 1080 px, four tabs and More on a phone. "Search or jump"
 * and ⌘K / Ctrl K open the CommandPalette, the second way in. Widths come from the shell's own box (a container),
 * so the /__ui board can draw it at 390 and 1280 side by side, and overlays open inside it.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { cx } from '../cx'
import { Link, useNav } from '../nav'
import { CommandPalette, type Command } from '../patterns/CommandPalette'
import { PortalContext } from '../portal'
import { Badge, Kbd } from '../primitives/Chip'
import { Button, IconButton } from '../primitives/Button'
import { ErrorBoundary } from '../primitives/ErrorBoundary'
import { Icon, type IconName } from '../primitives/Icon'
import s from './AppShell.module.css'

/** What the shell needs from a registry entry (src/sections.ts adds `load`). */
export interface NavSection {
  key: string
  path: string
  title: string
  description?: string
  icon: IconName
  /** The sidebar group; none puts it on top (Today). */
  group?: string
  /** Pinned to the phone's tab bar; four at most. */
  tab?: boolean
  /** A key into `counts`, drawn as a badge. */
  count?: string
  /** Signal badge: things that wait on a person (open alerts). Grey otherwise. */
  countTone?: 'down'
  /** Planned, not built: greyed with "later", never a link. */
  later?: boolean
  /** Dev only (the UI board). */
  dev?: boolean
  /** Built but not shipped: dev and /__ui only. */
  draft?: boolean
}

/** The section a path belongs to: the longest matching prefix; `/` only on its own. */
export function activeSection<T extends NavSection>(sections: readonly T[], pathname: string): T | undefined {
  return [...sections]
    .filter((x) => (x.path === '/' ? pathname === '/' : pathname === x.path || pathname.startsWith(`${x.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0]
}

/** Sections in sidebar order: the top ones, then each group in order of first appearance. */
export function groupSections<T extends NavSection>(sections: readonly T[]): { group: string | null; items: T[] }[] {
  const out: { group: string | null; items: T[] }[] = []
  for (const x of sections) {
    const g = x.group ?? null
    const at = out.find((o) => o.group === g)
    if (at) at.items.push(x)
    else if (g === null) out.unshift({ group: null, items: [x] })
    else out.push({ group: g, items: [x] })
  }
  return out
}

export function AppShell({
  sections,
  counts = {},
  where = 'Admin',
  user,
  onSignOut,
  commands = [],
  showDev = false,
  contained = false,
  hotkeys = true,
  banner,
  children,
}: {
  sections: readonly NavSection[]
  counts?: Record<string, number>
  where?: string
  user?: { email: string }
  onSignOut?: () => void
  /** More palette entries: founders, actions. Sections are added for you. */
  commands?: Command[]
  /** Show dev and draft sections (dev builds and /__ui). */
  showDev?: boolean
  /** Fill the parent's height instead of the viewport (the /__ui frames). */
  contained?: boolean
  /** Listen for ⌘K on the window. Off for a second shell on the same page. */
  hotkeys?: boolean
  /** A line above the sidebar's foot, e.g. "Mock · Example data". */
  banner?: ReactNode
  children: ReactNode
}) {
  const { where: at, navigate } = useNav()
  const [root, setRoot] = useState<HTMLDivElement | null>(null)
  const [palette, setPalette] = useState(false)
  const [more, setMore] = useState(false)

  const visible = useMemo(() => sections.filter((x) => showDev || (!x.dev && !x.draft)), [sections, showDev])
  const live = visible.filter((x) => !x.later)
  const current = activeSection(visible, at.pathname)
  const tabs = live.filter((x) => x.tab).slice(0, 4)
  const go = (path: string) => (setMore(false), navigate(path))

  const allCommands = useMemo<Command[]>(
    () => [
      ...live.map((x) => ({ id: `section:${x.key}`, label: x.title, group: 'Sections', icon: x.icon, keywords: x.description, hint: x.count && counts[x.count] ? String(counts[x.count]) : undefined, run: () => navigate(x.path) })),
      ...commands,
    ],
    [live, commands, counts, navigate],
  )

  useEffect(() => {
    if (!hotkeys) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') e.preventDefault(), setPalette((o) => !o)
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [hotkeys])

  const badge = (x: NavSection) => {
    const n = x.count ? counts[x.count] : 0
    return n ? <Badge count={n} tone={x.countTone} label={`${n} waiting`} /> : null
  }
  const navItem = (x: NavSection, place: 'side' | 'more') =>
    x.later ? (
      <span key={x.key} className={cx(s.nav, s.later)} aria-disabled="true" title={`${x.title}: later`}>
        <Icon name={x.icon} />
        <span className={s.lbl}>{x.title}</span>
        <span className={s.tag}>later</span>
      </span>
    ) : (
      <Link key={x.key} to={x.path} aria-current={current?.key === x.key ? 'page' : undefined} title={x.title} onClick={() => place === 'more' && setMore(false)}>
        <span className={cx(s.nav, current?.key === x.key && s.on)}>
          <Icon name={x.icon} />
          <span className={s.lbl}>{x.title}</span>
          {badge(x)}
          {(x.dev || x.draft) && <span className={s.tag}>{x.dev ? 'dev' : 'draft'}</span>}
        </span>
      </Link>
    )

  return (
    <div ref={setRoot} className={cx(s.root, contained && s.contained)}>
      <PortalContext.Provider value={root}>
        <div className={s.app}>
          <aside className={s.side} aria-label="Sections">
            <Link to="/" aria-label={`cpg.computer ${where}`}>
              <span className={s.brand}>
                <BrandMark />
                <span className={s.wm}>
                  cpg<span className={s.pt}>.</span>computer
                </span>
                <span className={s.where}>{where}</span>
              </span>
            </Link>
            <button type="button" className={s.jump} onClick={() => setPalette(true)} aria-label="Search or jump">
              <Icon name="search" size="sm" />
              <span className={s.jumpText}>Search or jump</span>
              <span className={s.jumpKey}>
                <Kbd>⌘K</Kbd>
              </span>
            </button>
            <nav className={s.groups} aria-label={where}>
              {groupSections(visible).map((g) => (
                <div key={g.group ?? 'top'} className={s.group}>
                  {g.group && <div className={s.groupName}>{g.group}</div>}
                  {g.items.map((x) => navItem(x, 'side'))}
                </div>
              ))}
            </nav>
            <div className={s.foot}>
              {banner && <div className={s.banner}>{banner}</div>}
              {user && (
                <div className={s.who}>
                  <span className={s.avatar} aria-hidden="true">
                    {user.email.slice(0, 1).toUpperCase()}
                  </span>
                  <span className={s.email}>{user.email}</span>
                </div>
              )}
              {onSignOut && (
                <span className={s.signout}>
                  <Button variant="ghost" size="xs" onClick={onSignOut}>
                    Sign out
                  </Button>
                </span>
              )}
            </div>
          </aside>

          <header className={s.topbar}>
            <Link to="/" aria-label={`cpg.computer ${where}`}>
              <span className={s.brand}>
                <BrandMark />
                <span className={s.where}>{where}</span>
              </span>
            </Link>
            <span className={s.topTitle}>{current?.title}</span>
            <IconButton icon="search" label="Search or jump" onClick={() => setPalette(true)} />
          </header>

          <main className={s.main}>
            <ErrorBoundary>{children}</ErrorBoundary>
          </main>

          <nav className={s.tabbar} aria-label="Tabs">
            {tabs.map((x) => (
              <button key={x.key} type="button" className={s.tab} aria-current={current?.key === x.key ? 'page' : undefined} onClick={() => go(x.path)}>
                <Icon name={x.icon} />
                {x.title}
                <span className={s.tabBadge}>{badge(x)}</span>
              </button>
            ))}
            <button type="button" className={s.tab} aria-haspopup="dialog" aria-current={current && !tabs.includes(current) ? 'page' : undefined} onClick={() => setMore(true)}>
              <Icon name="more" />
              More
            </button>
          </nav>
        </div>

        <Dialog.Root open={more} onOpenChange={setMore}>
          <Dialog.Portal container={root ?? undefined}>
            <Dialog.Content className={s.more} aria-describedby={undefined}>
              <div className={s.moreHead}>
                <Dialog.Title className={s.moreTitle}>More</Dialog.Title>
                <IconButton icon="close" label="Close" onClick={() => setMore(false)} />
              </div>
              <div className={s.moreBody}>
                <button type="button" className={s.jump} onClick={() => (setMore(false), setPalette(true))}>
                  <Icon name="search" size="sm" />
                  <span>Search or jump</span>
                </button>
                {groupSections(live).map((g) => (
                  <div key={g.group ?? 'top'} className={s.moreGroup}>
                    {g.group && <div className={s.groupName}>{g.group}</div>}
                    <div className={s.moreList}>{g.items.map((x) => navItem(x, 'more'))}</div>
                  </div>
                ))}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <CommandPalette open={palette} onOpenChange={setPalette} commands={allCommands} />
      </PortalContext.Provider>
    </div>
  )
}

/** The mark: a small machine with a power light (brand round 1). Drawn from tokens. The Brand page draws it large. */
export function BrandMark({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" className={s.mark}>
      <rect className={s.mCase} x="3" y="2" width="26" height="28" rx="7" strokeWidth="2" />
      <rect className={s.mGlass} x="7.5" y="6.5" width="17" height="13" rx="3.5" />
      <circle className={s.mLed} cx="9.5" cy="24.5" r="2" />
      <rect className={s.mSlot} x="15" y="23.5" width="9" height="2.2" rx="1.1" />
    </svg>
  )
}
